import { TooltipText } from 'components/shared';
import {
  DefaultButton,
  getTheme,
  IButtonStyles,
  IStackItemStyles,
  IStackStyles,
  mergeStyles,
  Stack,
  Text
} from '@fluentui/react';
import React, { useContext, useEffect, useState } from 'react';
import { ImageCarouselContentNode } from '../JsonContent';
import { QueueUxPreferences, SubjectImagePreferences } from 'services';
import { empty } from '../JsonFrame';
import { ImageBytesType } from './ImageBytesType';
import { ImageUrlType } from './ImageUrlType';
import { JsonContentValidationMessage } from './JsonContentValidationMessage';
import { ReviewContext } from 'components/review/ReviewContextProvider';
import { isDarkTheme } from 'theme';

interface ImageCarouselTypeProps {
  val: ImageCarouselContentNode[];
  focusText: string;
  isGridView: boolean;
  isZoomInActive?: boolean;
  uxPreferences?: QueueUxPreferences;
  subjectImagePreferences?: SubjectImagePreferences;
  checked?: boolean;
}

export const ImageCarouselType: React.FunctionComponent<ImageCarouselTypeProps> = (props): JSX.Element => {
  const { palette, fonts } = getTheme();
  const { state, dispatch } = useContext(ReviewContext);

  const carouselImgWrapperStyles: Partial<IStackStyles> = {
    root: {
      maxHeight: props.isZoomInActive ? 'calc(100% - 40px)' : '100%',
      minHeight: props.isZoomInActive ? 500 : ''
    }
  };

  const carouselNavigationWrapperStyles: Partial<IStackStyles> = {
    root: {
      height: 40,
      selectors: {
        '.ms-Button--default:focus': {
          boxShadow: 'none',
          outline: 'none'
        },
        '.is-disabled': {
          backgroundColor: palette.white
        }
      }
    }
  };

  const carouselLengthWrapperStyles: IStackItemStyles = {
    root: {
      height: 20,
      width: 35
    }
  };

  const buttonStyles = (): IButtonStyles => {
    const buttonStyles: IButtonStyles = {
      root: {
        padding: 0,
        borderWidth: 0,
        height: 20,
        minWidth: 50
      },
      icon: {
        marginLeft: 0,
        fontSize: fonts.mediumPlus.fontSize,
        lineHeight: fonts.mediumPlus.lineHeight,
        height: 20,
        color: isDarkTheme() ? palette.themeTertiary : palette.themePrimary
      },
      iconDisabled: {
        color: palette.neutralSecondary
      },
      textContainer: {
        maxWidth: 30
      },
      label: {
        margin: 0,
        fontFamily: fonts.mediumPlus.fontFamily,
        fontSize: fonts.mediumPlus.fontSize,
        lineHeight: fonts.mediumPlus.lineHeight,
        color: isDarkTheme() ? palette.themeTertiary : palette.themePrimary
      },
      labelDisabled: {
        color: palette.neutralSecondary
      },
      rootFocused: {
        boxShadow: 'none',
        outline: 'none'
      }
    };
    return buttonStyles;
  };

  const previousButtonStyles: IButtonStyles = buttonStyles();
  const nextButtonStyles: IButtonStyles = buttonStyles();
  nextButtonStyles.root = {
    padding: 0,
    borderWidth: 0,
    height: 20,
    minWidth: 50,
    transform: 'rotate( 180deg )',
    selectors: { '.ms-Button-textContainer': { transform: 'rotate( 180deg )' } }
  };

  const currentImgNumberCalss = mergeStyles({ width: 10 }, 'd-inline-block');
  const imageCarouselValidationMessageClass = 'pt-3 mt-4 ml-3';

  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const carouselSize = props.val.length - 1;
  const [isPreviousImg, setIsPreviousImg] = useState(false);
  const [isNextImg, setIsNextImg] = useState(carouselSize != 0);

  const previousImg = (): void => {
    const res = currentImgIndex - 1;
    if (res === 0) {
      setIsPreviousImg(false);
      setCurrentImgIndex(0);
    } else {
      setCurrentImgIndex(res);
    }
    setIsNextImg(true);
  };

  const nextImg = (): void => {
    const res = currentImgIndex + 1;
    if (res === carouselSize) {
      setCurrentImgIndex(carouselSize);
      setIsNextImg(false);
    } else {
      setCurrentImgIndex(res);
    }
    setIsPreviousImg(true);
  };

  const renderImg = (): JSX.Element => {
    const val = props.val[currentImgIndex].value;
    if (props.val[currentImgIndex].type === 'imageUrl') {
      return (
        <ImageUrlType
          isGridView={props.isGridView}
          value={val.value}
          caption={val.caption}
          isZoomInActive={props.isZoomInActive}
          subjectImagePreferences={props.subjectImagePreferences}
          uxPreferences={props.uxPreferences}
          extraHeight={props.val.length > 0 ? 40 : 0}
        />
      );
    }
    if (props.val[currentImgIndex].type === 'imageBytes') {
      return (
        <ImageBytesType
          isGridView={props.isGridView}
          value={val.value}
          caption={val.caption}
          isZoomInActive={props.isZoomInActive}
          subjectImagePreferences={props.subjectImagePreferences}
          uxPreferences={props.uxPreferences}
          extraHeight={props.val.length > 0 ? 40 : 0}
        />
      );
    }
    return (
      <JsonContentValidationMessage
        message={TooltipText.unsupportedTypeText + props.val[currentImgIndex].type}
        validationMessageWrapperClass={imageCarouselValidationMessageClass}
      />
    );
  };

  const circularNextImg = (): void => {
    const nextImgIndex = currentImgIndex + 1;
    if (nextImgIndex <= carouselSize) {
      nextImg();
    } else {
      setCurrentImgIndex(0);
      setIsNextImg(true);
      setIsPreviousImg(false);
    }
  };

  const resetToFirstImage = (): void => {
    setCurrentImgIndex(0);
    setIsPreviousImg(false);
    setIsNextImg(carouselSize != 0);
  };

  useEffect(() => {
    if (state?.carouselAnimationEnabled) {
      resetToFirstImage();
    }
  }, [state?.carouselAnimationEnabled]);

  useEffect(() => {
    let interval;
    if (state?.carouselAnimationEnabled && carouselSize >= 1) {
      const strSpeed = state.carouselAnimationSpeed.toString() + '000';
      interval = setInterval(circularNextImg, Number(strSpeed));
    }
    return () => clearInterval(interval);
  }, [currentImgIndex, state?.carouselAnimationEnabled, state?.carouselAnimationSpeed]);

  useEffect(() => {
    if (state?.nextImgCount > 0 && isNextImg && props.checked) {
      nextImg();
    }
  }, [state?.nextImgCount]);

  useEffect(() => {
    if (state?.previousImgCount > 0 && isPreviousImg && props.checked) {
      previousImg();
    }
  }, [state?.previousImgCount]);

  return (
    <>
      <Stack className="position-relative" styles={carouselImgWrapperStyles}>
        <Stack.Item className="h-100">
          {!empty(props.val[currentImgIndex]) ? (
            renderImg()
          ) : (
            <JsonContentValidationMessage
              message={TooltipText.imageCarouselTypeValidationMessage}
              validationMessageWrapperClass={imageCarouselValidationMessageClass}
            />
          )}
        </Stack.Item>
      </Stack>
      {props.val.length > 0 && (
        <Stack className="mx-3" verticalAlign="center" styles={carouselNavigationWrapperStyles}>
          <Stack horizontal>
            <Stack.Item align="center" styles={carouselLengthWrapperStyles}>
              <Text variant="mediumPlus" className={currentImgNumberCalss}>
                {currentImgIndex + 1}
              </Text>
              <Text variant="mediumPlus">of {carouselSize + 1}</Text>
            </Stack.Item>
            <Stack.Item className="ml-3">
              <DefaultButton
                iconProps={{ iconName: 'ChevronLeft' }}
                text="Prev"
                onClick={(): void => {
                  previousImg();
                  if (state?.carouselAnimationEnabled) {
                    dispatch({ type: 'DISABLE_CAROUSEL_ANIMATION' });
                  }
                }}
                styles={previousButtonStyles}
                className="mr-2 carousel"
                disabled={!isPreviousImg}
              />
              <DefaultButton
                text="Next"
                iconProps={{ iconName: 'ChevronLeft' }}
                onClick={(): void => {
                  nextImg();
                  if (state?.carouselAnimationEnabled) {
                    dispatch({ type: 'DISABLE_CAROUSEL_ANIMATION' });
                  }
                }}
                styles={nextButtonStyles}
                className="carousel"
                disabled={!isNextImg}
              />
            </Stack.Item>
          </Stack>
        </Stack>
      )}
    </>
  );
};
